// src/components/ProjectList.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim"; 
import '../App.css';

const API_URL = 'https://un-borders.com';

function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { serviceTitle } = useParams();
  const navigate = useNavigate();
  
  const [activeCategory, setActiveCategory] = useState('الكل');
  const [hoveredProjectId, setHoveredProjectId] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🔹 إعدادات الرمال
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    fullScreen: { enable: true, zIndex: -1 }, 
    fpsLimit: 60,
    particles: {
      number: { value: 100, density: { enable: true, value_area: 1000 } },
      color: { value: "#daa520" },
      shape: { type: "circle" },
      opacity: { value: 0.6, random: true },
      size: { value: { min: 1, max: 4 }, random: true },
      links: {
        enable: true, // 🟢 تم إضافة هذا الجزء لكي تظهر خطوط الجذب (السناب) مع الماوس
        color: "#daa520",
        distance: 150,
        opacity: 0.1, // شفافة قليلاً في الوضع العادي
        width: 1,
      },
      move: {
        enable: true,
        speed: 1,
        direction: "right",
        random: true,
        straight: false,
        outModes: { default: "out" },
      },
    },
    interactivity: {
      detectsOn: "window",
      events: {
        onClick: { enable: true, mode: "push" },
        onHover: { enable: true, mode: "grab" },
      },
      modes: {
        push: { quantity: 5 },
        grab: { distance: 150, links: { opacity: 0.8 } },
      },
    },
    detectRetina: true,
  };

  useEffect(() => {
    const encodedServiceTitle = serviceTitle ? encodeURIComponent(serviceTitle) : null;
    const requestUrl = encodedServiceTitle
      ? `${API_URL}/api/projects/?service=${encodedServiceTitle}`
      : `${API_URL}/api/projects/`;
    
    axios.get(requestUrl)
      .then(response => {
        setProjects(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("There was an error fetching the projects!", error);
        setLoading(false);
      });
  }, [serviceTitle]); 

  const preventActions = (e) => e.preventDefault();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e', color: '#daa520', fontSize: '1.8rem', fontWeight: 'bold' }}>
        جاري تهيئة المعرض السينمائي...
      </div>
    );
  }
  
  const pageTitle = serviceTitle ? `مشاريع ${decodeURIComponent(serviceTitle)}` : 'المعرض الهندسي';
  const uniqueCategories = ['الكل', ...new Set(projects.map(p => p.category_name || 'عام'))];
  
  const filteredProjects = activeCategory === 'الكل' 
    ? projects 
    : projects.filter(p => (p.category_name || 'عام') === activeCategory);

  const getImageUrl = (path) => path.startsWith('http') ? path : `${API_URL}${path}`;

  return (
    // 🔹 1. حل اللجة: width 100% بدون vw، مع قفل overflow-x
    <div className="projects-page" style={{ 
      position: 'relative', 
      minHeight: '100vh', 
      backgroundColor: '#1a1a2e', 
      userSelect: 'none', 
      WebkitUserSelect: 'none', 
      paddingBottom: '80px',
      overflowX: 'hidden', 
      width: '100%',
      boxSizing: 'border-box'
    }}>
      
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        
        .accordion-gallery {
          display: flex;
          flex-direction: row;
          height: 65vh;
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          gap: 12px;
          padding: 0 20px;
        }
        @media (max-width: 900px) {
          .accordion-gallery {
            flex-direction: column;
            height: auto; 
            padding: 0 15px;
          }
        }
        .image-protection-container, .decoy-trap {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"><path d="M4 2L11.4 21L14.7 14.7L21 11.4L4 2Z" fill="%23daa520" stroke="%23ffffff" stroke-width="1.5"/></svg>'), pointer !important;
        }
      `}</style>

      {/* 🔹 2. حل الرمل: position: fixed عشان يملى الشاشة كلها مهما نزلت بالسكرول */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}> 
        <Particles id="tsparticles" init={particlesInit} options={particlesOptions} style={{ width: '100vw', height: '100vh' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 10, paddingTop: '30px', width: '100%' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '30px', padding: '0 15px' }}>
          <motion.h1 
            style={{ color: '#daa520', fontSize: isMobile ? '2.2rem' : '3rem', textShadow: '0 4px 15px rgba(0,0,0,0.8)', marginBottom: '15px' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {pageTitle}
          </motion.h1>
          <button onClick={() => navigate('/services')} className="service-button" style={{ fontSize: '15px', padding: '8px 25px', backgroundColor: '#daa520', color: '#1a1a2e', border: 'none', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>
             الرجوع لقائمة الخدمات
          </button>
        </div>

        {uniqueCategories.length > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '40px',
            padding: '0 15px',
            width: '100%'
          }}>
            {uniqueCategories.map((category, index) => {
              const isActive = activeCategory === category;
              return (
                <motion.button
                  key={index}
                  onClick={() => {
                    setActiveCategory(category);
                    setHoveredProjectId(null); 
                  }}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    backgroundColor: isActive ? 'rgba(218,165,32,0.9)' : 'rgba(30, 35, 55, 0.7)',
                    borderColor: isActive ? '#ffdf00' : 'rgba(255, 255, 255, 0.1)',
                    color: isActive ? '#1a1a2e' : '#e0e0e0',
                    boxShadow: isActive ? '0 0 20px rgba(218, 165, 32, 0.5)' : '0 4px 10px rgba(0,0,0,0.3)',
                  }}
                  style={{
                    backdropFilter: 'blur(10px)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    padding: isMobile ? '8px 18px' : '10px 25px',
                    borderRadius: '25px',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'color 0.3s ease'
                  }}
                >
                  {category}
                </motion.button>
              );
            })}
          </div>
        )}

        <motion.div layout className="accordion-gallery">
          <AnimatePresence>
            {filteredProjects.length === 0 ? (
                <p style={{ textAlign: 'center', width: '100%', color: '#daa520', fontSize: '1.5rem', padding: '0 20px' }}>لا توجد مشاريع في هذا القسم حالياً.</p>
            ) : (
                filteredProjects.map((project) => {
                
                const isHovered = hoveredProjectId === project.id;
                const isAnyHovered = hoveredProjectId !== null;

                let flexValue = isMobile ? 'none' : 2;
                let blurValue = isMobile ? 'blur(0px)' : 'blur(0px)';
                let brightnessValue = isMobile ? 'brightness(1)' : 'brightness(0.85)';
                let isExpanded = isMobile ? true : false; 
                let cardHeight = isMobile ? '280px' : '100%'; 

                if (!isMobile) {
                  if (isHovered) {
                    flexValue = 6; 
                    blurValue = 'blur(0px)';
                    brightnessValue = 'brightness(1.1)'; 
                    isExpanded = true;
                  } else if (isAnyHovered) {
                    flexValue = 0.8; 
                    blurValue = 'blur(6px)'; 
                    brightnessValue = 'brightness(0.4)'; 
                  }
                }

                return (
                  <motion.div
                    layout 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ 
                      opacity: 1, 
                      scale: 1,
                      flex: flexValue,
                      filter: `${blurValue} ${brightnessValue}`,
                      height: cardHeight
                    }}
                    exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                    transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                    key={project.id}
                    className="image-protection-container"
                    onMouseEnter={() => !isMobile && setHoveredProjectId(project.id)}
                    onMouseLeave={() => !isMobile && setHoveredProjectId(null)}
                    // 🔹 معالجة الضغط على الكارت الخارجي
                    onClick={() => {
                      if (isMobile) {
                        setHoveredProjectId(isHovered ? null : project.id);
                      } else if (isHovered) {
                        navigate(`/projects/${project.id}`);
                      }
                    }}
                    style={{ 
                      position: 'relative',
                      borderRadius: '24px', 
                      overflow: 'hidden',
                      boxShadow: isExpanded ? '0 15px 40px rgba(0,0,0,0.6)' : '0 5px 15px rgba(0,0,0,0.3)',
                      border: isExpanded ? '1px solid rgba(218,165,32,0.4)' : '1px solid rgba(255,255,255,0.05)',
                      width: '100%',
                      zIndex: isHovered ? 20 : 1
                    }}
                  >
                    <img 
                      src={getImageUrl(project.main_image)} 
                      alt={project.title} 
                      className="protected-img"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onContextMenu={preventActions}
                      onDragStart={preventActions}
                    />
                    
                    {/* طبقات الحماية تحت النصوص */}
                    <div className="decoy-trap" style={{ zIndex: 5 }} onContextMenu={preventActions} onDragStart={preventActions}></div>
                    <div className="watermark-overlay" style={{ zIndex: 6 }}></div>

                    {/* 🔹 3. حل الضغط: إعطاء pointer-events: auto للنصوص والزراير و z-index أعلى */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div 
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.3 }}
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(26, 26, 46, 0.95) 0%, rgba(26, 26, 46, 0.7) 60%, transparent 100%)',
                            padding: '30px 20px 20px 20px',
                            color: '#fff',
                            textAlign: 'right',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            height: '50%',
                            zIndex: 10,
                            pointerEvents: 'auto' // 👈 السر هنا! تفعيل الضغط
                          }}
                        >
                          <h2 style={{ margin: '0 0 10px 0', fontSize: isMobile ? '1.4rem' : '1.6rem', color: '#daa520', textShadow: '0 2px 5px rgba(0,0,0,0.8)' }}>
                            {project.title}
                          </h2>
                          <p style={{ margin: '0 0 5px 0', fontSize: isMobile ? '0.9rem' : '1rem', color: '#e0e0e0' }}>
                            <strong>الموقع:</strong> {project.location}
                          </p>
                          
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); // يمنع تضارب الضغطة مع الكارت الخارجي
                              navigate(`/projects/${project.id}`); 
                            }}
                            style={{
                              marginTop: '15px',
                              padding: '10px 20px',
                              backgroundColor: '#daa520',
                              color: '#1a1a2e',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              alignSelf: isMobile ? 'center' : 'flex-start',
                              fontSize: isMobile ? '0.9rem' : '1rem',
                              pointerEvents: 'auto' // 👈 التأكيد على قابلية الضغط
                            }}
                          >
                            عرض التفاصيل والتصميم
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

export default ProjectList;